import type { TransactionStatus } from '@contracts/payment/TransactionResult';
import Info from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Stack } from '@src/common/components/Stack';
import { StatusBadge } from '@src/common/components/StatusBadge';
import { Tooltip } from '@src/common/components/Tooltip';
import { colors } from '@src/common/styles/valueTokens';
import type { Transaction } from '@src/orders/domain/Transaction';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type Colors = {
  color: string;
  border: string;
  background: string;
};

export const TransactionStatusCell = ({
  transaction,
}: {
  transaction: Transaction;
}) => {
  const error = transaction.error;

  if (error) {
    return (
      <FailedTransactionStatusCell transaction={transaction} error={error} />
    );
  }

  return <TransactionStatusBadge transaction={transaction} />;
};

const TransactionStatusBadge = ({
  transaction,
}: {
  transaction: Transaction;
}) => {
  const { t } = useTranslation();
  return (
    <StatusBadge
      size="small"
      text={t(`transactionStatus.${transaction.status}`)}
      color={TransactionStatusColor[transaction.status].color}
      borderColor={TransactionStatusColor[transaction.status].border}
      backgroundColor={TransactionStatusColor[transaction.status].background}
    />
  );
};

const FailedTransactionStatusCell = ({
  transaction,
  error,
}: {
  transaction: Transaction;
  error: string;
}) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Tooltip
      title={error}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Stack direction="row" gap={1}>
        <TransactionStatusBadge transaction={transaction} />
        {hovered && <Info style={{ fill: '#0F61AC' }} />}
        {!hovered && <InfoOutlinedIcon style={{ fill: '#0F61AC' }} />}
      </Stack>
    </Tooltip>
  );
};

const TransactionStatusColor: Record<TransactionStatus, Colors> = {
  SUCCESS: {
    color: colors.utility.success[700],
    border: colors.utility.success[200],
    background: colors.utility.success[50],
  },
  REFUSED: {
    color: colors.utility.error[700],
    border: colors.utility.error[200],
    background: colors.utility.error[50],
  },
  ERROR: {
    color: colors.primary[10],
    border: colors.state.primary.medium,
    background: colors.state.primary.light,
  },
  EXPIRED: {
    color: colors.utility.error[700],
    border: colors.utility.error[200],
    background: colors.utility.error[50],
  },
};
